/**
 * Saját <head>-kezelő (a react-helmet-async helyett).
 *
 * Miért: a react-helmet-async 2.x a példányát a render() alatt veszi fel, és a
 * componentWillUnmount-ban veszi ki. A React 18/19 StrictMode (és a Suspense elrejtés/
 * újramutatás) ugyanazon a példányon lefuttatja az unmount → remount párt, a remount
 * viszont nem veszi fel újra — így a később csatolt oldalak (termék, kategória…) címe és
 * meta-tagjei eltűntek, a dupla eltávolítás (indexOf -1 → splice(-1)) pedig egy ártatlan
 * példányt (pl. az alapértékeket) is kidobott.
 *
 * Itt a regisztráció effectben történik (felvétel a setupban, kivétel a cleanupban),
 * így StrictMode alatt is szimmetrikus. A sorrend a komponens első renderelésekor kapott
 * sorszám: a szülő a gyereke előtt renderel, a később csatolt oldal később — tehát a
 * legmélyebb / legutóbbi bejegyzés nyer (mint a Helmetnél).
 */

/** Megegyezik a public/index.html <title>-jével és meta leírásával. */
export const DEFAULT_TITLE = 'GT Kreatív Sarok - Egyedi termékek személyre szabással';
export const DEFAULT_DESCRIPTION =
  'GT Kreatív Sarok - Minőségi egyedi termékek személyre szabással. Pólók, bögrék, táskák felirattal és logóval.';
export const DEFAULT_KEYWORDS = 'egyedi póló, felirat, bögre, táska, ajándék, személyre szabott';

/** Az általunk kezelt <head>-elemek jelölője; értéke az elem dedup-kulcsa. */
export const HEAD_ATTRIBUTE = 'data-head';

export type HeadMeta =
  | { name: string; content: string; property?: never }
  | { property: string; content: string; name?: never };

export interface HeadLink {
  rel: string;
  href: string;
}

export interface HeadSpec {
  title?: string;
  meta?: HeadMeta[];
  links?: HeadLink[];
  /** Kész (szerializált) application/ld+json tartalmak; ezek nem írják felül egymást. */
  jsonLd?: string[];
}

interface Entry {
  order: number;
  seq: number;
  spec: HeadSpec;
}

const BASE: HeadSpec = {
  title: DEFAULT_TITLE,
  meta: [
    { name: 'description', content: DEFAULT_DESCRIPTION },
    { name: 'keywords', content: DEFAULT_KEYWORDS },
  ],
};

const entries = new Map<symbol, Entry>();
let orderCounter = 0;
let seqCounter = 0;

/** Renderelési sorszám: a komponens első renderelésekor kéri (useState inicializáló). */
export const nextHeadOrder = (): number => {
  orderCounter += 1;
  return orderCounter;
};

const metaKey = (meta: HeadMeta): string =>
  meta.name !== undefined ? `name:${meta.name}` : `property:${meta.property}`;

interface ResolvedHead {
  title: string;
  meta: Map<string, HeadMeta>;
  links: Map<string, HeadLink>;
  jsonLd: string[];
}

const resolve = (): ResolvedHead => {
  const sorted = Array.from(entries.values()).sort((a, b) => a.order - b.order || a.seq - b.seq);
  const resolved: ResolvedHead = { title: DEFAULT_TITLE, meta: new Map(), links: new Map(), jsonLd: [] };
  [BASE, ...sorted.map((e) => e.spec)].forEach((spec) => {
    if (spec.title !== undefined) resolved.title = spec.title;
    spec.meta?.forEach((m) => resolved.meta.set(metaKey(m), m));
    spec.links?.forEach((l) => resolved.links.set(`rel:${l.rel}`, l));
    if (spec.jsonLd) resolved.jsonLd.push(...spec.jsonLd);
  });
  return resolved;
};

/** A kívánt elemeket (kulcs → attribútumok/tartalom) összehangolja a <head> meglévőivel. */
const reconcile = (
  tag: 'meta' | 'link' | 'script',
  desired: Map<string, { attrs: Record<string, string>; text?: string }>,
): void => {
  const head = document.head;
  const existing = new Map<string, Element>();
  head.querySelectorAll(`${tag}[${HEAD_ATTRIBUTE}]`).forEach((el) => {
    const key = el.getAttribute(HEAD_ATTRIBUTE) ?? '';
    if (existing.has(key) || !desired.has(key)) el.remove();
    else existing.set(key, el);
  });
  desired.forEach(({ attrs, text }, key) => {
    let el = existing.get(key);
    if (!el) {
      el = document.createElement(tag);
      el.setAttribute(HEAD_ATTRIBUTE, key);
      head.appendChild(el);
    }
    const target = el;
    Object.entries(attrs).forEach(([attr, value]) => {
      if (target.getAttribute(attr) !== value) target.setAttribute(attr, value);
    });
    if (text !== undefined && target.textContent !== text) target.textContent = text;
  });
};

const apply = (): void => {
  if (typeof document === 'undefined') return;
  const { title, meta, links, jsonLd } = resolve();
  if (document.title !== title) document.title = title;

  const metaTags = new Map<string, { attrs: Record<string, string> }>();
  meta.forEach((m, key) => {
    const attrs: Record<string, string> =
      m.name !== undefined ? { name: m.name, content: m.content } : { property: m.property, content: m.content };
    metaTags.set(key, { attrs });
  });
  reconcile('meta', metaTags);

  const linkTags = new Map<string, { attrs: Record<string, string> }>();
  links.forEach((l, key) => linkTags.set(key, { attrs: { rel: l.rel, href: l.href } }));
  reconcile('link', linkTags);

  const scripts = new Map<string, { attrs: Record<string, string>; text: string }>();
  jsonLd.forEach((json, i) => scripts.set(`ld:${i}`, { attrs: { type: 'application/ld+json' }, text: json }));
  reconcile('script', scripts);
};

/** Bejegyzés felvétele; a visszaadott függvény veszi ki (effect-cleanupként). */
export const addHeadEntry = (order: number, spec: HeadSpec): (() => void) => {
  const token = Symbol('head-entry');
  seqCounter += 1;
  entries.set(token, { order, seq: seqCounter, spec });
  apply();
  return () => {
    entries.delete(token);
    apply();
  };
};
